import React, { useState, useEffect } from 'react';
import './AdminPanel.css';
import { FaPlus, FaTrash, FaEdit, FaSignOutAlt, FaCheckCircle, FaClock, FaTruck } from 'react-icons/fa';
import { API_URL, IMAGE_BASE_URL } from '../config';

const AdminPanel = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    badge: 'Popüler',
    image: null
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Helper function to get image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    // If it's already a full URL (starts with http), return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    // Otherwise, prepend backend URL
    return `${IMAGE_BASE_URL}/${imageUrl}`;
  };

  // Ürünleri Yükle
  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'orders') {
      fetchOrders();
    }
  }, [activeTab]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}?action=get_products`);
      
      // Check if response is OK and is JSON
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error('API returned HTML instead of JSON:', text.substring(0, 200));
        throw new Error('API returned HTML instead of JSON. Check backend URL and file location.');
      }
      
      const data = await response.json();
      if (data.success) {
        setProducts(data.data || []);
      }
    } catch (error) {
      console.error('Ürünler yükleme hatası:', error);
      console.error('API URL:', API_URL);
    }
    setLoading(false);
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}?action=get_orders`);
      const data = await response.json();
      if (data.success) {
        setOrders(data.data || []);
      }
    } catch (error) {
      console.error('Siparişler yükleme hatası:', error);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      alert('Ürün adı ve fiyatı zorunludur');
      return;
    }

    setLoading(true);
    const formDataObj = new FormData();
    formDataObj.append('name', formData.name);
    formDataObj.append('description', formData.description);
    formDataObj.append('price', formData.price);
    formDataObj.append('badge', formData.badge);
    if (formData.image) {
      formDataObj.append('image', formData.image);
    }

    try {
      const response = await fetch(`${API_URL}?action=add_product`, {
        method: 'POST',
        body: formDataObj
      });
      const data = await response.json();
      
      if (data.success) {
        alert('Ürün başarıyla eklendi!');
        setFormData({
          name: '',
          description: '',
          price: '',
          badge: 'Popüler',
          image: null
        });
        fetchProducts();
      } else {
        alert('Hata: ' + data.message);
      }
    } catch (error) {
      alert('Ürün eklenirken hata oluştu');
      console.error(error);
    }
    setLoading(false);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}?action=delete_product&id=${id}`);
      const data = await response.json();
      
      if (data.success) {
        alert('Ürün başarıyla silindi!');
        fetchProducts();
      } else {
        alert('Hata: ' + data.message);
      }
    } catch (error) {
      alert('Ürün silinirken hata oluştu');
      console.error(error);
    }
    setLoading(false);
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    const formData = new FormData();
    formData.append('id', orderId);
    formData.append('status', newStatus);

    try {
      const response = await fetch(`${API_URL}?action=update_order_status`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      
      if (data.success) {
        alert('Sipariş durumu güncellendi!');
        fetchOrders();
      } else {
        alert('Hata: ' + data.message);
      }
    } catch (error) {
      alert('Sipariş güncellenirken hata oluştu');
      console.error(error);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Beklemede',
      confirmed: 'Onaylandı',
      delivered: 'Teslim Edildi',
      cancelled: 'İptal Edildi'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#FFA500',
      confirmed: '#4CAF50',
      delivered: '#2196F3',
      cancelled: '#f44336'
    };
    return colors[status] || '#666';
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="admin-title">
          <h1>🍖 Resume Çiğköfte Admin Paneli</h1>
        </div>
        <button className="logout-btn" onClick={onLogout}>
          <FaSignOutAlt /> Çıkış
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          📦 Ürünler
        </button>
        <button
          className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          🛒 Siparişler ({orders.length})
        </button>
      </div>

      {activeTab === 'products' && (
        <div className="admin-content products-section">
          <div className="product-form">
            <h2><FaPlus /> Yeni Ürün Ekle</h2>
            <form onSubmit={handleAddProduct}>
              <div className="form-row">
                <input
                  type="text"
                  name="name"
                  placeholder="Ürün Adı"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <input
                  type="number"
                  name="price"
                  placeholder="Fiyat (₺)"
                  value={formData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  required
                />
              </div>
              <textarea
                name="description"
                placeholder="Ürün Açıklaması"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
              />
              <div className="form-row">
                <select
                  name="badge"
                  value={formData.badge}
                  onChange={handleInputChange}
                >
                  <option>Popüler</option>
                  <option>Paket</option>
                  <option>Özel</option>
                  <option>Lüks</option>
                  <option>Yeni</option>
                </select>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </div>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Yükleniyor...' : 'Ürün Ekle'}
              </button>
            </form>
          </div>

          <div className="products-list">
            <h2>Mevcut Ürünler ({products.length})</h2>
            {products.length === 0 ? (
              <p className="no-data">Henüz ürün eklenmemiş</p>
            ) : (
              <div className="products-grid">
                {products.map(product => (
                  <div key={product.id} className="product-item">
                    {product.image_url && (
                      <img src={getImageUrl(product.image_url)} alt={product.name} />
                    )}
                    <div className="product-item-info">
                      <h3>{product.name}</h3>
                      <p className="price">₺{parseFloat(product.price).toFixed(2)}</p>
                      {product.badge && <span className="badge">{product.badge}</span>}
                      <p className="description">{product.description}</p>
                    </div>
                    <div className="product-actions">
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteProduct(product.id)}
                      >
                        <FaTrash /> Sil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="admin-content orders-section">
          <h2>Tüm Siparişler</h2>
          {orders.length === 0 ? (
            <p className="no-data">Henüz sipariş yok</p>
          ) : (
            <div className="orders-table-wrapper">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Müşteri</th>
                    <th>Telefon</th>
                    <th>Ürün</th>
                    <th>Miktar</th>
                    <th>Fiyat</th>
                    <th>Ödeme</th>
                    <th>Durum</th>
                    <th>Tarih</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.id}>
                      <td>#{order.id}</td>
                      <td>
                        <strong>{order.customer_name}</strong>
                        <small>{order.customer_email}</small>
                      </td>
                      <td>{order.customer_phone}</td>
                      <td>{order.product_name}</td>
                      <td className="text-center">{order.quantity}</td>
                      <td>₺{parseFloat(order.total_price).toFixed(2)}</td>
                      <td>{order.payment_method === 'cash' ? 'Kapıda Ödeme' : 'Kartla'}</td>
                      <td>
                        <select
                          value={order.order_status}
                          onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                          className="status-select"
                          style={{ borderColor: getStatusColor(order.order_status) }}
                        >
                          <option value="pending">Beklemede</option>
                          <option value="confirmed">Onaylandı</option>
                          <option value="delivered">Teslim Edildi</option>
                          <option value="cancelled">İptal Edildi</option>
                        </select>
                      </td>
                      <td>{new Date(order.created_at).toLocaleDateString('tr-TR')}</td>
                      <td>
                        <button className="view-btn">Detaylar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
